import React, { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { useDispatch, useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import { getSiteData, applyAdmission, submitContact } from '../store/actions'
import { Carousel } from 'react-responsive-carousel'
import "react-responsive-carousel/lib/styles/carousel.min.css"
import founderImg from '../assets/founder.jpg'
import buildingImg from '../assets/building.jpg'
import { baseURL2 } from '../helper/helper'
import { ArrowRight } from 'lucide-react'

export default function Home() {
    const dispatch = useDispatch()
    const [admissionData, setAdmissionData] = useState({
        student_name: "",
        contact_number: "",
        course_selected: ""
    })
    const [contactData, setContactData] = useState({
        name: "",
        email: "",
        phone: "",
        message: ""
    })

    const handleAdmissionSubmit = async (e) => {
        e.preventDefault()
        try {
            await dispatch(applyAdmission(admissionData))
            toast.success("Admission application submitted successfully!")
            setAdmissionData({ student_name: "", contact_number: "", course_selected: "" })
        } catch (error) {
            toast.error("Failed to submit admission application.")
        }
    }

    const handleContactSubmit = async (e) => {
        e.preventDefault()
        try {
            await dispatch(submitContact(contactData))
            toast.success("Contact message sent successfully!")
            setContactData({ name: "", email: "", phone: "", message: "" })
        } catch (error) {
            toast.error("Failed to send contact message.")
        }
    }
    const { banners, institutions } = useSelector((state) => state.reducer || {})

    useEffect(() => {
        dispatch(getSiteData())
    }, [dispatch])

    return (
        <main className="min-h-screen w-full bg-white text-gray-900">
            {/* Full Width Carousel */}
            <section className="w-full bg-primary/5 overflow-hidden flex flex-col">
                {banners && banners.length > 0 ? (
                    <Carousel
                        autoPlay
                        infiniteLoop
                        showStatus={false}
                        showThumbs={false}
                        interval={5000}
                        transitionTime={800}
                        className="w-full"
                        swipeable={true}
                        emulateTouch={true}
                    >
                        {banners.map((banner, index) => (
                            <div key={banner.id || index} className="w-full flex">
                                <img
                                    loading={index === 0 ? "eager" : "lazy"}
                                    src={baseURL2 + banner.image}
                                    alt={banner.title}
                                    className="w-full h-auto object-contain"
                                />
                            </div>
                        ))}
                    </Carousel>
                ) : (
                    <div className="w-full h-[50vh] flex items-center justify-center">
                        <span className="text-secondary/50 tracking-widest uppercase text-sm">Loading...</span>
                    </div>
                )}
            </section>

            {/* Founder Section */}
            <section className="py-24 px-6 md:px-12 max-w-7xl mx-auto flex flex-col md:flex-row gap-16 items-center border-b border-gray-100">
                <div className="w-full md:w-1/2">
                    <div className="aspect-[3/4] overflow-hidden bg-primary/5 rounded-tr-[80px] rounded-bl-[80px]">
                        <img
                            loading="lazy"
                            src={founderImg}
                            alt="Founder"
                            className="w-full h-full object-cover opacity-90 transition-opacity hover:opacity-100 duration-500"
                        />
                    </div>
                </div>
                <div className="w-full md:w-1/2">
                    <span className="text-xs tracking-widest uppercase text-secondary mb-4 block font-semibold">The Visionary</span>
                    <h2 className="text-3xl md:text-5xl font-light text-primary mb-8 leading-tight">
                        Hazrat Hafiz o Qari Maolana Sibtain Raza Ashrafi Sabri Sahab
                    </h2>
                    <div className="w-12 h-px bg-secondary mb-8"></div>
                    <p className="text-gray-600 text-lg leading-relaxed font-light mb-8">
                        Serving the community of Nashik for over five years, the visionary founder of the Faiz-ul-Hasnain Foundation integrates technology, commerce, and character into a single developmental platform. He is a leader who doesn't just change lives—he illuminates futures.
                    </p>
                </div>
            </section>

            {/* Institutions Section */}
            <section className="py-24 px-6 md:px-12 bg-primary/5">
                <div className="max-w-7xl mx-auto">
                    <div className="mb-16">
                        <span className="text-xs tracking-widest uppercase text-secondary font-semibold mb-2 block">Our Campus</span>
                        <h2 className="text-3xl md:text-4xl text-primary font-light mb-4">Our Institutions</h2>
                        <div className="w-12 h-px bg-secondary"></div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                        {institutions && institutions.length > 0 ? (
                            institutions.map((inst, index) => (
                                <div key={inst.id || index} className="group bg-white rounded-[40px] shadow-sm hover:shadow-xl transition-all duration-300 border border-transparent hover:border-primary/20 overflow-hidden flex flex-col">
                                    <div className="aspect-[16/9] overflow-hidden bg-gray-100 relative">
                                        <img
                                            loading="lazy"
                                            src={inst.photo ? (baseURL2 + inst.photo) : buildingImg}
                                            alt={inst.name}
                                            className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                                        />
                                        <div className="absolute inset-0 bg-primary/10 group-hover:bg-transparent transition-colors"></div>
                                    </div>
                                    <div className="p-8 flex-1 flex flex-col">
                                        <h4 className="text-xl font-medium text-primary mb-3">{inst.name}</h4>
                                        <p className="text-gray-500 text-sm leading-relaxed mb-6 line-clamp-3">
                                            {inst.description || "An educational institution operating under the esteemed guidance of the Faiz Ul Hasnain Foundation."}
                                        </p>
                                        <div className="mt-auto pt-4 flex">
                                            <Link
                                                to={`/institution/${inst.slug}`}
                                                className="text-xs font-semibold uppercase tracking-widest text-secondary flex items-center gap-2 hover:text-primary transition-colors"
                                            >
                                                View Details <ArrowRight size={14} />
                                            </Link>
                                        </div>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="col-span-full text-center py-20 text-gray-400 text-sm tracking-widest uppercase">
                                Loading Institutions...
                            </div>
                        )}
                    </div>
                </div>
            </section>

            {/* Initiatives & Courses */}
            <section className="py-24 px-6 md:px-12 max-w-7xl mx-auto border-t border-gray-100">
                <div className="flex flex-col md:flex-row gap-16 items-center">
                    <div className="w-full md:w-1/2">
                        <span className="text-xs tracking-widest uppercase text-secondary font-semibold mb-2 block">Skill Development & Welfare</span>
                        <h2 className="text-3xl md:text-5xl font-light text-primary mb-6 leading-tight">
                            Our Courses & <br /> Initiatives
                        </h2>
                        <div className="w-12 h-px bg-secondary mb-8"></div>
                        <p className="text-gray-600 text-lg leading-relaxed font-light mb-6">
                            We operate a wide range of specialized courses and community welfare programs aimed at equipping individuals with real-world skills and providing essential humanitarian aid.
                        </p>
                        <ul className="flex flex-wrap gap-3 mt-8">
                            {['Computer Classes', 'Digital Marketing', 'Technical Courses', 'Work From Home Training', 'Online Learning', 'Free Dispensary', 'Tree Plantation', 'Women Safety'].map((item, i) => (
                                <li key={i} className="px-4 py-2 bg-primary/5 text-primary border border-primary/10 rounded-full text-sm font-medium shadow-sm">
                                    {item}
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div className="w-full md:w-1/2 grid grid-cols-2 gap-4">
                        <div className="bg-primary/5 rounded-[40px] p-8 aspect-square flex flex-col justify-center items-center text-center hover:-translate-y-2 transition-transform border border-primary/10">
                            <span className="text-4xl text-primary mb-4 font-light border-b-2 border-secondary pb-2">01</span>
                            <h4 className="text-lg font-medium text-primary">Technical <br/> Education</h4>
                        </div>
                        <div className="bg-secondary/10 rounded-[40px] p-8 aspect-square mt-12 flex flex-col justify-center items-center text-center hover:-translate-y-2 transition-transform border border-secondary/20">
                            <span className="text-4xl text-secondary mb-4 font-light border-b-2 border-primary pb-2">02</span>
                            <h4 className="text-lg font-medium text-primary">Community <br/> Welfare</h4>
                        </div>
                    </div>
                </div>
            </section>

            {/* Courses Showcase */}
            <section className="py-24 px-6 md:px-12 max-w-7xl mx-auto border-t border-gray-100">
                <div className="mb-16 text-center">
                    <span className="text-xs tracking-widest uppercase text-secondary font-semibold mb-2 block">Curriculum</span>
                    <h2 className="text-3xl md:text-4xl text-primary font-light mb-4">Academic Offerings</h2>
                    <div className="w-12 h-px bg-secondary mx-auto"></div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-12">
                    {institutions?.flatMap(inst => inst.courses || [])
                        .filter((course, index, self) => index === self.findIndex((t) => t.name === course.name))
                        .map((course, idx) => (
                            <div key={idx} className="flex flex-col group p-6 rounded-2xl hover:bg-primary/5 transition-colors">
                                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center text-primary mb-6 font-bold text-lg group-hover:bg-primary group-hover:text-white transition-colors">
                                    {idx + 1}
                                </div>
                                <h3 className="text-lg font-medium text-primary mb-3">
                                    {course.name}
                                </h3>
                                <p className="text-gray-500 text-sm leading-relaxed mb-6 font-light">
                                    {course.description || "Learn in-depth about principles and modern sciences through our dedicated curriculum."}
                                </p>
                            </div>
                        ))}
                </div>
            </section>

            {/* Admission & Contact Dual Forms */}
            <section className="py-24 px-6 md:px-12 bg-primary border-t-4 border-secondary text-white">
                <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
                    {/* Admission Form */}
                    <div className="bg-white/5 p-8 md:p-12 rounded-[40px] border border-white/10 backdrop-blur-sm">
                        <div className="mb-12">
                            <span className="text-xs tracking-widest uppercase text-secondary mb-2 block font-semibold">Join Us</span>
                            <h3 className="text-3xl text-white font-light mb-4">Admissions Registry</h3>
                            <p className="text-white/60 text-sm font-light">Join our academic session with specialized courses.</p>
                        </div>
                        <form className="space-y-6" onSubmit={handleAdmissionSubmit}>
                            <div>
                                <input
                                    type="text"
                                    placeholder="Full Name"
                                    className="w-full bg-transparent border-b border-white/20 py-3 px-2 text-sm text-white placeholder-white/40 focus:border-secondary outline-none transition-colors"
                                    value={admissionData.student_name}
                                    onChange={(e) => setAdmissionData({ ...admissionData, student_name: e.target.value })}
                                    required
                                />
                            </div>
                            <div>
                                <input
                                    type="tel"
                                    placeholder="Contact Number"
                                    className="w-full bg-transparent border-b border-white/20 py-3 px-2 text-sm text-white placeholder-white/40 focus:border-secondary outline-none transition-colors"
                                    value={admissionData.contact_number}
                                    onChange={(e) => setAdmissionData({ ...admissionData, contact_number: e.target.value })}
                                    required
                                />
                            </div>
                            <div>
                                <select
                                    className="w-full bg-transparent border-b border-white/20 py-3 px-2 text-sm text-white/80 focus:border-secondary outline-none transition-colors"
                                    value={admissionData.course_selected}
                                    onChange={(e) => setAdmissionData({ ...admissionData, course_selected: e.target.value })}
                                    required
                                >
                                    <option value="" className="text-gray-900">Select a Course</option>
                                    {institutions?.flatMap(inst => inst.courses || []).map((c, i) => (
                                        <option key={i} value={c.id} className="text-gray-900">{c.name}</option>
                                    ))}
                                </select>
                            </div>
                            <button type="submit" className="w-full py-4 mt-8 bg-secondary text-white font-bold rounded-xl text-sm tracking-widest uppercase hover:bg-yellow-500 transition-colors shadow-lg shadow-secondary/20">
                                Submit Application
                            </button>
                        </form>
                    </div>

                    {/* Contact Form */}
                    <div className="bg-white/5 p-8 md:p-12 rounded-[40px] border border-white/10 backdrop-blur-sm">
                        <div className="mb-12">
                            <span className="text-xs tracking-widest uppercase text-secondary mb-2 block font-semibold">Message Us</span>
                            <h3 className="text-3xl text-white font-light mb-4">Direct Inquiry</h3>
                            <p className="text-white/60 text-sm font-light">Speak with our administration for institutional queries.</p>
                        </div>
                        <form className="space-y-6" onSubmit={handleContactSubmit}>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <input
                                    type="text"
                                    placeholder="Your Name"
                                    className="w-full bg-transparent border-b border-white/20 py-3 px-2 text-sm text-white placeholder-white/40 focus:border-secondary outline-none transition-colors"
                                    value={contactData.name}
                                    onChange={(e) => setContactData({ ...contactData, name: e.target.value })}
                                    required
                                />
                                <input
                                    type="email"
                                    placeholder="Your Email"
                                    className="w-full bg-transparent border-b border-white/20 py-3 px-2 text-sm text-white placeholder-white/40 focus:border-secondary outline-none transition-colors"
                                    value={contactData.email}
                                    onChange={(e) => setContactData({ ...contactData, email: e.target.value })}
                                    required
                                />
                            </div>
                            <input
                                type="text"
                                placeholder="Phone Number"
                                className="w-full bg-transparent border-b border-white/20 py-3 px-2 text-sm text-white placeholder-white/40 focus:border-secondary outline-none transition-colors"
                                value={contactData.phone}
                                onChange={(e) => setContactData({ ...contactData, phone: e.target.value })}
                                required
                            />
                            <textarea
                                rows="3"
                                placeholder="Message..."
                                className="w-full bg-transparent border-b border-white/20 py-3 px-2 text-sm text-white placeholder-white/40 focus:border-secondary outline-none transition-colors resize-none"
                                value={contactData.message}
                                onChange={(e) => setContactData({ ...contactData, message: e.target.value })}
                                required
                            ></textarea>
                            <button type="submit" className="w-full py-4 mt-8 bg-transparent border border-secondary text-secondary font-bold rounded-xl hover:bg-secondary hover:text-white transition-colors text-sm tracking-widest uppercase">
                                Send Message
                            </button>
                        </form>
                    </div>
                </div>
            </section>
        </main>
    )
}
